import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import moment from "moment";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCopy,
  FaDownload,
  FaSearch,
} from "react-icons/fa";
import { customColors } from "../../../atoms/customColors";
import { componentState } from "../../../atoms/componentState";
import BolepixGestaoAPI, {
  AnaliticoGestao,
  JanelasResumo,
  LinhaGestao,
  ResumoGestao,
  SITUACAO,
  Situacao,
} from "../../../services/BolepixGestao";
import Pagination from "../../../defaultComponents/pagination";

/**
 * GESTÃO DE BOLETOS — a tela que responde as três perguntas do cliente:
 * o que vai vencer, o que venceu e não pagaram, e o que foi pago.
 *
 * A régua da data MUDA conforme a aba, e isso é dito em texto na tela porque é
 * exatamente onde o cliente se perde: no carnê, o boleto é gerado meses antes
 * do vencimento e pago depois dele. Filtrar por data de criação — que era o
 * padrão da listagem antiga — não responde nenhuma das três perguntas.
 *
 *   A vencer / Vencidos -> data de VENCIMENTO
 *   Recebidos           -> data do CRÉDITO no extrato (quando o dinheiro caiu)
 *
 * Os contadores vêm somados do servidor. Somar a página daria 15.
 */

const fmtBRL = (n?: number | null) =>
  (Number.isFinite(n as number) ? (n as number) : 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtData = (d?: string | null) => (d ? moment(d).format("DD/MM/YYYY") : "—");

const hoje = () => moment().format("YYYY-MM-DD");

type PresetId = "7" | "15" | "30" | "mes" | "mesPassado" | "tudo" | "custom";

const PRESETS: Array<{ id: PresetId; label: string; para: Situacao[] }> = [
  { id: "7", label: "Próximos 7 dias", para: [SITUACAO.A_VENCER] },
  { id: "15", label: "Próximos 15 dias", para: [SITUACAO.A_VENCER] },
  { id: "30", label: "Próximos 30 dias", para: [SITUACAO.A_VENCER] },
  { id: "tudo", label: "Tudo em aberto", para: [SITUACAO.VENCIDO] },
  { id: "30", label: "Últimos 30 dias", para: [SITUACAO.VENCIDO, SITUACAO.PAGO] },
  { id: "mes", label: "Este mês", para: [SITUACAO.VENCIDO, SITUACAO.PAGO] },
  { id: "mesPassado", label: "Mês passado", para: [SITUACAO.VENCIDO, SITUACAO.PAGO] },
];

function intervalo(preset: PresetId, situacao: Situacao) {
  const futuro = situacao === SITUACAO.A_VENCER;
  switch (preset) {
    case "7":
    case "15":
    case "30": {
      const dias = Number(preset);
      return futuro
        ? { de: hoje(), ate: moment().add(dias, "day").format("YYYY-MM-DD") }
        : { de: moment().subtract(dias, "day").format("YYYY-MM-DD"), ate: hoje() };
    }
    case "mes":
      return {
        de: moment().startOf("month").format("YYYY-MM-DD"),
        ate: moment().endOf("month").format("YYYY-MM-DD"),
      };
    case "mesPassado":
      return {
        de: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        ate: moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      };
    // "tudo": sem período — o servidor usa o padrão da situação (dívida velha
    // não pode sumir só porque o filtro do mês mudou)
    default:
      return {};
  }
}

const PRESET_PADRAO: Record<Situacao, PresetId> = {
  [SITUACAO.A_VENCER]: "30",
  [SITUACAO.VENCIDO]: "tudo",
  [SITUACAO.PAGO]: "mes",
};

export default function GestaoBoletos() {
  const [colors] = useRecoilState(customColors);
  const setHeaderLocation = useSetRecoilState(componentState);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const situacaoInicial = (searchParams.get("situacao") as Situacao) || SITUACAO.A_VENCER;

  const [situacao, setSituacao] = useState<Situacao>(situacaoInicial);
  const [preset, setPreset] = useState<PresetId>(PRESET_PADRAO[situacaoInicial]);
  const [de, setDe] = useState(searchParams.get("de") || "");
  const [ate, setAte] = useState(searchParams.get("ate") || "");
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");

  const [linhas, setLinhas] = useState<LinhaGestao[]>([]);
  const [resumo, setResumo] = useState<ResumoGestao | null>(null);
  const [janelas, setJanelas] = useState<JanelasResumo | null>(null);
  const [analitico, setAnalitico] = useState<AnaliticoGestao | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [verAnalise, setVerAnalise] = useState(false);
  const [verAjuda, setVerAjuda] = useState(true);

  // A análise tem período PRÓPRIO, independente da aba. Ela responde uma
  // pergunta de competência ("dos boletos que venceram entre X e Y, quantos
  // foram pagos"), enquanto o card de vencidos responde "quanto está em aberto
  // hoje, de qualquer data". Amarrar as duas coisas ao mesmo filtro fazia a
  // tela mostrar 105 vencidos em cima e 7 vencimentos na análise, sem nada
  // explicando a diferença.
  const [presetAnalise, setPresetAnalise] =
    useState<"mes" | "mesPassado" | "90" | "ano" | "mesEscolhido">("mes");
  // Mês fechado escolhido à mão (YYYY-MM). Comparar mês a mês é a leitura
  // natural de carnê: "agosto pagou melhor que julho?".
  const [mesAnalise, setMesAnalise] = useState(moment().format("YYYY-MM"));

  const periodoAnalise = useMemo(() => {
    const mesFechado = (base: moment.Moment) => ({
      de: base.clone().startOf("month").format("YYYY-MM-DD"),
      ate: base.clone().endOf("month").format("YYYY-MM-DD"),
    });

    switch (presetAnalise) {
      case "mesPassado":
        return mesFechado(moment().subtract(1, "month"));
      case "mesEscolhido": {
        const escolhido = moment(mesAnalise + "-01", "YYYY-MM-DD");
        return mesFechado(escolhido.isValid() ? escolhido : moment());
      }
      case "90":
        return {
          de: moment().subtract(90, "day").format("YYYY-MM-DD"),
          ate: hoje(),
        };
      case "ano":
        return {
          de: moment().startOf("year").format("YYYY-MM-DD"),
          ate: hoje(),
        };
      default:
        // "este mês" também é mês FECHADO (1º ao último dia): o denominador da
        // taxa de pagamento precisa ser o mês inteiro, senão a conversão sobe
        // artificialmente só porque o mês ainda não acabou.
        return mesFechado(moment());
    }
  }, [presetAnalise, mesAnalise]);

  const periodo = useMemo(() => {
    if (preset === "custom") {
      const p: { de?: string; ate?: string } = {};
      if (de) p.de = de;
      if (ate) p.ate = ate;
      return p;
    }
    return intervalo(preset, situacao);
  }, [preset, de, ate, situacao]);

  const presetsDaAba = PRESETS.filter((p) => p.para.includes(situacao));

  useEffect(() => {
    setHeaderLocation("Gestão de Boletos");
  }, [setHeaderLocation]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // O resumo vai SEM período de propósito: cada card tem a janela dele
      // (a vencer 30 dias à frente, vencido tudo em aberto, recebido no mês).
      // Amarrar os cards ao filtro da aba fazia "vencidos" abrir zerado, porque
      // a janela em foco na abertura é futura.
      const [lista, res] = await Promise.all([
        BolepixGestaoAPI.getLista(situacao, periodo, page, pageSize, buscaAplicada),
        BolepixGestaoAPI.getResumo({}, buscaAplicada),
      ]);
      setLinhas(lista.registros || []);
      setTotalPaginas(lista.totalPaginas || 1);
      setTotalRegistros(lista.totalRegistros || 0);
      setResumo(res.resumo);
      setJanelas(res.janelas);
    } catch (e) {
      toast({
        title: "Não foi possível carregar os boletos.",
        status: "error",
        duration: 4000,
      });
    } finally {
      setCarregando(false);
    }
  }, [situacao, periodo, page, pageSize, buscaAplicada, toast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!verAnalise) return;
    BolepixGestaoAPI.getAnalitico(periodoAnalise)
      .then(setAnalitico)
      .catch(() => setAnalitico(null));
  }, [verAnalise, periodoAnalise]);

  function trocarAba(nova: Situacao) {
    setSituacao(nova);
    setPreset(PRESET_PADRAO[nova]);
    setPage(1);
  }

  async function exportar() {
    setExportando(true);
    try {
      await BolepixGestaoAPI.exportar(situacao, periodo, buscaAplicada);
    } catch (e) {
      toast({ title: "Falha ao gerar o relatório.", status: "error" });
    } finally {
      setExportando(false);
    }
  }

  function copiar(texto?: string | null) {
    if (!texto) return;
    navigator.clipboard?.writeText(texto);
    toast({ title: "Linha digitável copiada.", status: "success", duration: 2000 });
  }

  const ehPago = situacao === SITUACAO.PAGO;

  // "01/09 a 30/09" — o período de cada card, escrito embaixo dele. Contador
  // sem janela visível é contador em que ninguém confia.
  const rotuloJanela = (j?: { de: string; ate: string }) =>
    j
      ? `${moment(j.de).format("DD/MM/YY")} a ${moment(j.ate).format("DD/MM/YY")}`
      : "";

  const explicacao = ehPago
    ? "Recebidos são os boletos cujo dinheiro CAIU NA CONTA no período, pela data do crédito no extrato. Um boleto que venceu em janeiro e foi pago hoje entra no caixa de HOJE, e continua sendo do vencimento de janeiro. O crédito costuma cair no dia seguinte ao pagamento."
    : situacao === SITUACAO.VENCIDO
      ? "Vencidos são os boletos que passaram da data de vencimento e continuam sem pagamento."
      : "A vencer são os boletos ainda dentro do prazo, pela data de vencimento.";

  return (
    <Flex
      flexDir="column"
      height="90vh"
      width="100%"
      bgColor={colors.GRAY_LIGHTEST}
      p={{ base: 3, md: 5, xl: 8 }}
      overflowX="hidden"
      overflowY="auto"
    >
      {/* ---------------- cabeçalho ---------------- */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={4}>
        <Text
          fontSize={{ base: "lg", md: "xl", "2xl": "2xl" }}
          fontWeight="semibold"
          color={colors.BLUEMONEDDBACKGROUND}
        >
          Gestão de Boletos
        </Text>
        <Flex gap={2} wrap="wrap">
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            leftIcon={<FaCalendarAlt />}
            onClick={() => navigate("/vendas/bolepix/calender")}
          >
            Calendário
          </Button>
          <Button
            size="sm"
            variant={verAnalise ? "solid" : "outline"}
            colorScheme="blue"
            onClick={() => setVerAnalise(!verAnalise)}
          >
            Análise
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate("/vendas/bolepix")}
          >
            Voltar
          </Button>
        </Flex>
      </Flex>

      {/* As três datas de um boleto, escritas na tela. É a origem de quase toda
          confusão do carnê: emissão, vencimento e crédito são três dias
          diferentes, e cada número desta tela usa um deles. */}
      <Box
        bg={colors.WHITE}
        borderRadius="lg"
        boxShadow="sm"
        borderLeftWidth="4px"
        borderLeftColor={colors.BLUEMONEDDBUTTONS}
        p={{ base: 3, md: 4 }}
        mb={4}
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <Text fontSize="sm" fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND}>
            As três datas de um boleto
          </Text>
          <Button
            size="xs"
            variant="link"
            colorScheme="blue"
            onClick={() => setVerAjuda(!verAjuda)}
          >
            {verAjuda ? "ocultar" : "entenda"}
          </Button>
        </Flex>

        {verAjuda && (
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={3}
            mt={3}
          >
            <DataExplicada
              titulo="Emissão"
              texto="quando o boleto foi gerado. Num carnê, isso acontece meses antes do vencimento — por isso procurar por esta data não ajuda a saber o que foi pago."
            />
            <DataExplicada
              titulo="Vencimento"
              texto="o prazo que o pagador tem. É a data que manda nas abas A vencer e Vencidos, e nas barras azuis do gráfico."
            />
            <DataExplicada
              titulo="Crédito (pagamento)"
              texto="o dia em que o dinheiro entrou na sua conta, pelo extrato. É a data da aba Recebidos e das barras verdes. Costuma cair 1 dia depois do pagamento."
            />
          </Grid>
        )}
      </Box>

      {/* ---------------- análise (opcional) ---------------- */}
      {verAnalise && (
        <PainelAnalise
          analitico={analitico}
          colors={colors}
          preset={presetAnalise}
          setPreset={setPresetAnalise}
          mes={mesAnalise}
          setMes={setMesAnalise}
          periodo={periodoAnalise}
        />
      )}
      <hr />
      <br />
      {/* ---------------- contadores ---------------- */}
      <Grid
        templateColumns={{
          base: "1fr",
          md: "repeat(2, 1fr)",
          xl: "repeat(4, 1fr)",
        }}
        gap={3}
        mb={4}
      >
        <CardContador
          titulo="A vencer"
          descricao={`próximos 30 dias · ${rotuloJanela(janelas?.aVencer)}`}
          qtde={resumo?.qtdeAVencer}
          valor={resumo?.valorAVencer}
          cor={colors.YELLOWMONEDD}
          ativo={situacao === SITUACAO.A_VENCER}
          onClick={() => trocarAba(SITUACAO.A_VENCER)}
        />
        <CardContador
          titulo="Vencidos"
          descricao="tudo em aberto, inclusive de meses anteriores"
          qtde={resumo?.qtdeVencido}
          valor={resumo?.valorVencido}
          cor={colors.REDMONEDD}
          ativo={situacao === SITUACAO.VENCIDO}
          onClick={() => trocarAba(SITUACAO.VENCIDO)}
        />
        <CardContador
          titulo="Recebidos"
          descricao={`caiu na conta · ${rotuloJanela(janelas?.pago)}`}
          qtde={resumo?.qtdePago}
          valor={resumo?.valorPago}
          detalhe={
            resumo && resumo.qtdePago > 0
              ? `líquido ${fmtBRL(resumo.valorLiquidoPago)}`
              : undefined
          }
          cor={colors.GREENMONEDD}
          ativo={situacao === SITUACAO.PAGO}
          onClick={() => trocarAba(SITUACAO.PAGO)}
        />
        <CardContador
          titulo="Ainda não emitidos"
          descricao={`sem boleto gerado · ${rotuloJanela(janelas?.aVencer)}`}
          qtde={resumo?.qtdeNaoEmitida}
          valor={resumo?.valorNaoEmitido}
          cor={colors.GRAY_DARK}
        />
      </Grid>

      {/* De onde veio o caixa da janela. É a pergunta que o carnê torna
          inevitável: o dinheiro que entrou hoje pode ser de vencimento
          antigo. Sem isso, o total do mês é lido como cobrança do mês. */}
      {ehPago && resumo && resumo.qtdePago > 0 && (
        <Box
          bg={colors.WHITE}
          borderRadius="lg"
          boxShadow="sm"
          borderLeftWidth="4px"
          borderLeftColor={colors.GREENMONEDD}
          p={{ base: 3, md: 4 }}
          mb={4}
        >
          <Text fontSize="sm" fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND}>
            De onde veio o dinheiro
          </Text>
          <Text fontSize="xs" color={colors.GRAY_DARK} mb={2}>
            vencimento dos boletos que entraram em{" "}
            {rotuloJanela(janelas?.pago)}
          </Text>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={3}
          >
            <OrigemCaixa
              rotulo="Venceram nesta janela"
              qtde={resumo.qtdeRecebidoDoPeriodo}
              valor={resumo.valorRecebidoDoPeriodo}
              cor={colors.GREENMONEDD}
            />
            <OrigemCaixa
              rotulo="Vencimento anterior (pagos em atraso)"
              qtde={resumo.qtdeRecebidoEmAtraso}
              valor={resumo.valorRecebidoEmAtraso}
              cor={colors.REDMONEDD}
            />
            <OrigemCaixa
              rotulo="Vencimento futuro (adiantados)"
              qtde={resumo.qtdeRecebidoAdiantado}
              valor={resumo.valorRecebidoAdiantado}
              cor={colors.YELLOWMONEDD}
            />
          </Grid>
        </Box>
      )}


      {/* ---------------- filtros ---------------- */}
      <Box
        bg={colors.WHITE}
        borderRadius="lg"
        boxShadow="sm"
        border="1px solid"
        borderColor={colors.GRAY_LIGHTER}
        p={{ base: 3, md: 4 }}
        mb={4}
      >
        <Flex gap={3} wrap="wrap" align="flex-end">
          <Box minW="200px" flex="0 0 auto">
            <Text fontSize="xs" color={colors.GRAY_DARK} mb={1}>
              Período
            </Text>
            <Select
              size="sm"
              value={preset}
              onChange={(e) => {
                setPreset(e.target.value as PresetId);
                setPage(1);
              }}
            >
              {presetsDaAba.map((p) => (
                <option key={`${p.id}-${p.label}`} value={p.id}>
                  {p.label}
                </option>
              ))}
              <option value="custom">Escolher datas…</option>
            </Select>
          </Box>

          {preset === "custom" && (
            <>
              <Box>
                <Text fontSize="xs" color={colors.GRAY_DARK} mb={1}>
                  De
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={de}
                  onChange={(e) => {
                    setDe(e.target.value);
                    setPage(1);
                  }}
                />
              </Box>
              <Box>
                <Text fontSize="xs" color={colors.GRAY_DARK} mb={1}>
                  Até
                </Text>
                <Input
                  size="sm"
                  type="date"
                  value={ate}
                  onChange={(e) => {
                    setAte(e.target.value);
                    setPage(1);
                  }}
                />
              </Box>
            </>
          )}

          <Box flex="1 1 240px" minW="200px">
            <Text fontSize="xs" color={colors.GRAY_DARK} mb={1}>
              Buscar
            </Text>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <FaSearch color={colors.GRAY_DARK} />
              </InputLeftElement>
              <Input
                placeholder="documento, nome ou CPF/CNPJ do pagador"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setBuscaAplicada(busca);
                    setPage(1);
                  }
                }}
              />
            </InputGroup>
          </Box>

          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => {
              setBuscaAplicada(busca);
              setPage(1);
            }}
          >
            Pesquisar
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            leftIcon={<FaDownload />}
            isLoading={exportando}
            onClick={exportar}
          >
            Exportar
          </Button>
        </Flex>

        <Text fontSize="xs" color={colors.GRAY_DARK} mt={3}>
          {explicacao} O período acima filtra <b>a lista</b>; os cartões do topo
          mantêm a janela própria de cada um.
        </Text>
      </Box>

      {/* ---------------- lista ---------------- */}
      <Box
        flex="1"
        minH={{ base: "360px", md: "420px" }}
        display="flex"
        flexDirection="column"
        bg={colors.WHITE}
        borderRadius="lg"
        boxShadow="md"
        p={{ base: 3, md: 5 }}
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap={2} mb={3}>
          <Text fontWeight="semibold" color={colors.BLUEMONEDDBACKGROUND}>
            {totalRegistros} boleto(s)
          </Text>
          <Select
            size="sm"
            w="90px"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
        </Flex>

        {carregando ? (
          <Flex flex="1" minH="240px" align="center" justify="center">
            <Spinner size="xl" color={colors.BLUEMONEDDBUTTONS} thickness="4px" />
          </Flex>
        ) : linhas.length === 0 ? (
          <Flex flex="1" minH="240px" align="center" justify="center" px={4}>
            <Text color={colors.GRAY_DARK} textAlign="center">
              Nenhum boleto {ehPago ? "recebido" : situacao === SITUACAO.VENCIDO ? "vencido" : "a vencer"} no período
              selecionado.
            </Text>
          </Flex>
        ) : (
          <TableContainer flex="1" minH={0} overflowX="auto" overflowY="auto">
            <Table size="sm" variant="simple" minWidth={ehPago ? "1100px" : "1000px"}>
              <Thead>
                <Tr>
                  <Th>
                    {ehPago ? "Entrou na conta" : "Vence em"}
                    <Text
                      as="span"
                      display="block"
                      fontSize="2xs"
                      textTransform="none"
                      fontWeight="normal"
                      color={colors.GRAY_DARK}
                    >
                      {ehPago ? "data do crédito no extrato" : "data de vencimento"}
                    </Text>
                  </Th>
                  <Th>
                    {ehPago ? "Venceu em" : "Situação"}
                    {ehPago && (
                      <Text
                        as="span"
                        display="block"
                        fontSize="2xs"
                        textTransform="none"
                        fontWeight="normal"
                        color={colors.GRAY_DARK}
                      >
                        prazo original do boleto
                      </Text>
                    )}
                  </Th>
                  {ehPago && <Th>Pagamento</Th>}
                  <Th>Documento</Th>
                  <Th>Pagador</Th>
                  <Th>CPF/CNPJ</Th>
                  <Th isNumeric>Valor</Th>
                  {ehPago && <Th isNumeric>Líquido</Th>}
                  <Th>Linha digitável</Th>
                </Tr>
              </Thead>
              <Tbody>
                {linhas.map((l) => (
                  <Tr key={l.id} _hover={{ bg: colors.GRAY_LIGHTEST }}>
                    <Td whiteSpace="nowrap">
                      {fmtData(ehPago ? l.dataCredito : l.dataVencimento)}
                    </Td>
                    <Td whiteSpace="nowrap">
                      {ehPago ? (
                        fmtData(l.dataVencimento)
                      ) : (
                        <BadgeSituacao linha={l} />
                      )}
                    </Td>
                    {ehPago && (
                      <Td whiteSpace="nowrap">
                        <BadgePagamento dias={l.dias} />
                      </Td>
                    )}
                    <Td>{l.numeroDocumento || "—"}</Td>
                    <Td maxW="220px" overflow="hidden" textOverflow="ellipsis">
                      {l.nomePagador || "—"}
                      {l.idLote && (
                        <Text as="span" fontSize="xs" color={colors.GRAY_DARK}>
                          {" "}
                          · parcela {l.parcela}
                        </Text>
                      )}
                    </Td>
                    <Td whiteSpace="nowrap">{l.cpfPagador || "—"}</Td>
                    <Td isNumeric whiteSpace="nowrap">
                      {fmtBRL(l.valor)}
                    </Td>
                    {ehPago && (
                      <Td isNumeric whiteSpace="nowrap">
                        <Tooltip
                          label={`taxa ${fmtBRL(l.valor - (l.valorLiquido ?? 0))}`}
                        >
                          <span>{fmtBRL(l.valorLiquido)}</span>
                        </Tooltip>
                      </Td>
                    )}
                    <Td>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        leftIcon={<FaCopy />}
                        isDisabled={!l.linhaDigitavel}
                        onClick={() => copiar(l.linhaDigitavel)}
                      >
                        copiar
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        <Flex justify="center" align="center" wrap="wrap" gap={4} mt={4}>
          <Pagination
            currentPage={page}
            setCurrentPage={setPage}
            pages={totalPaginas}
            pageSize={pageSize}
          />
        </Flex>
      </Box>
    </Flex>
  );
}

/* ------------------------------------------------------------------ */

function CardContador({
  titulo,
  descricao,
  qtde,
  valor,
  detalhe,
  cor,
  ativo,
  onClick,
}: {
  titulo: string;
  descricao: string;
  qtde?: number;
  valor?: number;
  detalhe?: string;
  cor: string;
  ativo?: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      bg="white"
      borderRadius="lg"
      borderWidth="1px"
      borderColor={ativo ? cor : "gray.200"}
      borderLeftWidth="5px"
      borderLeftColor={cor}
      boxShadow={ativo ? "md" : "sm"}
      px={4}
      py={3}
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
      transition="all 0.15s"
      _hover={onClick ? { boxShadow: "md" } : undefined}
    >
      <Text fontSize="xs" fontWeight="bold" color="gray.600">
        {titulo.toUpperCase()}
      </Text>
      <Text fontSize={{ base: "xl", "2xl": "2xl" }} fontWeight="bold" color={cor}>
        {fmtBRL(valor)}
      </Text>
      <Text fontSize="xs" color="gray.500">
        {qtde ?? 0} boleto(s) · {descricao}
      </Text>
      {detalhe && (
        <Text fontSize="xs" color="gray.600" mt={1}>
          {detalhe}
        </Text>
      )}
    </Box>
  );
}

function DataExplicada({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="bold" color="gray.700">
        {titulo}
      </Text>
      <Text fontSize="xs" color="gray.600">
        {texto}
      </Text>
    </Box>
  );
}

function OrigemCaixa({
  rotulo,
  qtde,
  valor,
  cor,
}: {
  rotulo: string;
  qtde: number;
  valor: number;
  cor: string;
}) {
  return (
    <Box borderLeftWidth="3px" borderLeftColor={cor} pl={3}>
      <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
        {rotulo}
      </Text>
      <Text fontSize="lg" fontWeight="bold" color={cor}>
        {fmtBRL(valor)}
      </Text>
      <Text fontSize="2xs" color="gray.500">
        {qtde} boleto(s)
      </Text>
    </Box>
  );
}

/**
 * Atraso do pagamento, medido do vencimento até o CRÉDITO. O banco credita no
 * dia seguinte ao pagamento, então 1 dia é boleto pago em dia — por isso a
 * faixa de tolerância, e não um "atrasado" a partir de zero.
 */
function BadgePagamento({ dias }: { dias: number | null }) {
  if (dias == null) return <Text fontSize="xs">—</Text>;
  if (dias < 0)
    return (
      <Badge colorScheme="blue" variant="subtle">
        adiantado {Math.abs(dias)}d
      </Badge>
    );
  if (dias <= 1)
    return (
      <Badge colorScheme="green" variant="subtle">
        em dia
      </Badge>
    );
  return (
    <Badge colorScheme="orange" variant="subtle">
      {dias - 1} dia(s) de atraso
    </Badge>
  );
}

function BadgeSituacao({ linha }: { linha: LinhaGestao }) {
  if (linha.situacao === "VENCIDO") {
    return (
      <Badge colorScheme="red" variant="subtle">
        {linha.dias} dia(s) em atraso
      </Badge>
    );
  }
  if ((linha.dias ?? 0) === 0) {
    return (
      <Badge colorScheme="orange" variant="subtle">
        vence hoje
      </Badge>
    );
  }
  return (
    <Badge colorScheme="yellow" variant="subtle">
      vence em {linha.dias} dia(s)
    </Badge>
  );
}

function PainelAnalise({
  analitico,
  colors,
  preset,
  setPreset,
  mes,
  setMes,
  periodo,
}: {
  analitico: AnaliticoGestao | null;
  colors: any;
  preset: string;
  setPreset: (p: any) => void;
  mes: string;
  setMes: (m: string) => void;
  periodo: { de: string; ate: string };
}) {
  if (!analitico) {
    return (
      <Box bg={colors.WHITE} borderRadius="lg" boxShadow="sm" p={4} mb={4}>
        <Spinner size="sm" color={colors.BLUEMONEDDBUTTONS} />
      </Box>
    );
  }

  const { indicadores, aging, devedores, serie } = analitico;
  const maiorAging = Math.max(1, ...aging.map((a) => a.valor));
  const totalAging = aging.reduce(
    (acc, a) => ({ qtde: acc.qtde + a.qtde, valor: acc.valor + a.valor }),
    { qtde: 0, valor: 0 }
  );

  return (
    <Grid
      templateColumns={{ base: "1fr", xl: "1fr 1fr" }}
      gap={3}
      mb={4}
      alignItems="stretch"
    >
      <Box
        bg={colors.WHITE}
        borderRadius="lg"
        boxShadow="sm"
        p={4}
        gridColumn={{ base: "auto", xl: "1 / -1" }}
      >
        <Flex justify="space-between" align="baseline" wrap="wrap" gap={2} mb={1}>
          <Text fontSize="sm" fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND}>
            Previsto x recebido, dia a dia ({moment(periodo.de).format("DD/MM")}{" "}
            a {moment(periodo.ate).format("DD/MM")})
          </Text>
          <Flex gap={4} fontSize="xs" color={colors.GRAY_DARK} wrap="wrap">
            <Flex align="center" gap={1}>
              <Box w="10px" h="10px" borderRadius="2px" bg={colors.BLUEMONEDDBACKGROUND} />
              <b>vence</b>&nbsp;neste dia
            </Flex>
            <Flex align="center" gap={1}>
              <Box w="10px" h="10px" borderRadius="2px" bg={colors.GREENMONEDD} />
              <b>entrou na conta</b>&nbsp;neste dia
            </Flex>
          </Flex>
        </Flex>
        <Text fontSize="xs" color={colors.GRAY_DARK} mb={3}>
          Cada coluna é um dia. As duas barras do mesmo dia quase nunca são os
          mesmos boletos: a azul é o que <b>vencia</b> naquele dia, a verde é o
          dinheiro que <b>entrou</b> naquele dia — e ele pode ser de um boleto
          que venceu semanas ou meses antes. Passe o mouse para ver os dois
          números do dia.
        </Text>
        <SerieBarras serie={serie} colors={colors} />
      </Box>

      <Box bg={colors.WHITE} borderRadius="lg" boxShadow="sm" p={4}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={2} mb={1}>
          <Text fontSize="sm" fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND}>
            Desempenho da cobrança
          </Text>
          <Flex gap={2} align="center" wrap="wrap">
            <Select
              size="xs"
              w="150px"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              <option value="mes">Este mês</option>
              <option value="mesPassado">Mês passado</option>
              <option value="mesEscolhido">Escolher mês…</option>
              <option value="90">Últimos 90 dias</option>
              <option value="ano">Este ano</option>
            </Select>

            {/* mês fechado: o input devolve YYYY-MM e a tela usa do 1º ao
                último dia daquele mês */}
            {preset === "mesEscolhido" && (
              <Input
                size="xs"
                type="month"
                w="140px"
                max={moment().format("YYYY-MM")}
                value={mes}
                onChange={(e) => setMes(e.target.value)}
              />
            )}
          </Flex>
        </Flex>
        <Text fontSize="xs" color={colors.GRAY_DARK} mb={3}>
          Recorte por <b>vencimento</b>, de{" "}
          {moment(periodo.de).format("DD/MM/YYYY")} a{" "}
          {moment(periodo.ate).format("DD/MM/YYYY")}: dos boletos que venceram
          nessa faixa, quantos foram pagos. Este período é independente do
          filtro da lista acima.
        </Text>
        <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(4, 1fr)" }} gap={3}>
          <Indicador
            rotulo="Taxa de pagamento"
            valor={
              indicadores.taxaConversao != null
                ? `${indicadores.taxaConversao}%`
                : "—"
            }
          />
          <Indicador
            rotulo="Venceram no recorte"
            valor={`${indicadores.qtdeVencidaNoPeriodo}`}
            detalhe={fmtBRL(indicadores.valorVencidoNoPeriodo)}
          />
          <Indicador
            rotulo="Destes, foram pagos"
            valor={`${indicadores.qtdePagaDoPeriodo}`}
            detalhe={fmtBRL(indicadores.valorPagoDoPeriodo)}
          />
          <Indicador
            rotulo="Crédito em"
            valor={
              indicadores.diasAteCreditoMedio != null
                ? `${Number(indicadores.diasAteCreditoMedio).toFixed(1)} dia(s)`
                : "—"
            }
            detalhe="após o vencimento"
          />
        </Grid>

        <Divider my={4} />

        <Text fontSize="sm" fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND} mb={1}>
          Há quanto tempo estão em aberto
        </Text>
        <Text fontSize="xs" color={colors.GRAY_DARK} mb={2}>
          Fotografia de <b>hoje</b>, de vencimento antigo ou recente: não depende
          do recorte acima. O total bate com o cartão <b>Vencidos</b> —{" "}
          {totalAging.qtde} boleto(s), {fmtBRL(totalAging.valor)}.
        </Text>
        {aging.length === 0 ? (
          <Text fontSize="xs" color={colors.GRAY_DARK}>
            Nada vencido em aberto.
          </Text>
        ) : (
          aging.map((a) => (
            <Box key={a.faixa} mb={2}>
              <Flex justify="space-between" fontSize="xs" color={colors.GRAY_DARKER}>
                <Text>{a.rotulo}</Text>
                <Text>
                  {a.qtde} · {fmtBRL(a.valor)}
                </Text>
              </Flex>
              <Box bg="gray.100" borderRadius="full" h="8px" mt={1}>
                <Box
                  bg={colors.REDMONEDD}
                  borderRadius="full"
                  h="8px"
                  w={`${Math.round((a.valor / maiorAging) * 100)}%`}
                />
              </Box>
            </Box>
          ))
        )}
      </Box>

      <Box bg={colors.WHITE} borderRadius="lg" boxShadow="sm" p={4}>
        <Text fontSize="sm" fontWeight="bold" color={colors.BLUEMONEDDBACKGROUND} mb={1}>
          Quem está devendo
        </Text>
        <Text fontSize="xs" color={colors.GRAY_DARK} mb={3}>
          Maiores valores vencidos <b>em aberto hoje</b>, de qualquer
          vencimento — a fila da cobrança. Também não depende do recorte.
        </Text>
        {devedores.length === 0 ? (
          <Text fontSize="xs" color={colors.GRAY_DARK}>
            Ninguém em atraso.
          </Text>
        ) : (
          <TableContainer overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Pagador</Th>
                  <Th isNumeric>Boletos</Th>
                  <Th isNumeric>Valor</Th>
                  <Th isNumeric>Maior atraso</Th>
                </Tr>
              </Thead>
              <Tbody>
                {devedores.map((d, i) => (
                  <Tr key={`${d.cpfPagador}-${i}`}>
                    <Td maxW="200px" overflow="hidden" textOverflow="ellipsis">
                      {d.nomePagador || d.cpfPagador || "—"}
                    </Td>
                    <Td isNumeric>{d.qtdeVencida}</Td>
                    <Td isNumeric>{fmtBRL(d.valorVencido)}</Td>
                    <Td isNumeric>{d.maiorAtraso} dia(s)</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Grid>
  );
}

/**
 * Previsto x recebido, dia a dia.
 *
 * As duas barras do mesmo dia NÃO são os mesmos boletos e isso é dito na
 * legenda, no eixo e no tooltip:
 *   azul  = valor que VENCE naquele dia   (data de vencimento do boleto)
 *   verde = valor que ENTROU naquele dia  (data do crédito no extrato)
 *
 * A data embaixo da coluna girava e ficava cortada quando o período tinha
 * muitos dias: o rótulo não cabia nos 30px da coluna. Agora a coluna tem
 * largura mínima legível, a área de rótulo tem altura própria (não é mais
 * espremida pela altura fixa do gráfico) e, em períodos longos, o rótulo
 * inclina em vez de encolher.
 */
function SerieBarras({ serie, colors }: { serie: AnaliticoGestao["serie"]; colors: any }) {
  if (!serie || serie.length === 0) {
    return (
      <Text fontSize="xs" color={colors.GRAY_DARK}>
        Sem movimento no período.
      </Text>
    );
  }

  // Escala única para as duas barras: com escalas separadas, um dia de R$ 50
  // recebidos pareceria maior que um de R$ 5.000 previstos.
  const maior = Math.max(
    1,
    ...serie.map((d) => Math.max(d.valorPrevisto, d.valorRecebido))
  );

  const denso = serie.length > 12;
  const larguraColuna = denso ? 34 : 48;
  const alturaBarras = 130;
  // Espaço reservado para o rótulo. Inclinado ele ocupa mais altura — é o que
  // faltava antes, e por isso a data aparecia cortada.
  const alturaRotulo = denso ? 46 : 22;

  return (
    <Flex align="stretch" gap={2}>
      {/* eixo de valor: sem ele o cliente não sabe o que a altura significa */}
      <Flex
        direction="column"
        justify="space-between"
        flexShrink={0}
        h={`${alturaBarras}px`}
        pr={1}
        borderRight="1px solid"
        borderColor="gray.200"
      >
        <Text fontSize="2xs" color={colors.GRAY_DARK}>
          {fmtBRL(maior)}
        </Text>
        <Text fontSize="2xs" color={colors.GRAY_DARK}>
          {fmtBRL(maior / 2)}
        </Text>
        <Text fontSize="2xs" color={colors.GRAY_DARK}>
          R$ 0
        </Text>
      </Flex>

      <Box overflowX="auto" overflowY="hidden" flex="1" pb={1}>
        <Flex
          align="flex-end"
          gap={denso ? 1 : 2}
          minW={`${serie.length * (larguraColuna + (denso ? 4 : 8))}px`}
        >
          {serie.map((d) => (
            <Tooltip
              key={d.data}
              hasArrow
              label={
                `${moment(d.data).format("DD/MM/YYYY")}\n` +
                `Vence neste dia: ${fmtBRL(d.valorPrevisto)} em ${d.qtdePrevista} boleto(s)\n` +
                `Entrou na conta neste dia: ${fmtBRL(d.valorRecebido)} em ${d.qtdeRecebida} boleto(s)` +
                (d.valorLiquidoRecebido > 0
                  ? ` (líquido ${fmtBRL(d.valorLiquidoRecebido)})`
                  : "")
              }
              whiteSpace="pre-line"
            >
              <Flex
                direction="column"
                align="center"
                flex="0 0 auto"
                w={`${larguraColuna}px`}
              >
                <Flex align="flex-end" justify="center" gap="3px" h={`${alturaBarras}px`} w="100%">
                  <Box
                    w={denso ? "10px" : "14px"}
                    bg={colors.BLUEMONEDDBACKGROUND}
                    borderTopRadius="3px"
                    h={`${Math.max(
                      (d.valorPrevisto / maior) * 100,
                      d.valorPrevisto > 0 ? 3 : 0
                    )}%`}
                  />
                  <Box
                    w={denso ? "10px" : "14px"}
                    bg={colors.GREENMONEDD}
                    borderTopRadius="3px"
                    h={`${Math.max(
                      (d.valorRecebido / maior) * 100,
                      d.valorRecebido > 0 ? 3 : 0
                    )}%`}
                  />
                </Flex>

                <Box h={`${alturaRotulo}px`} w="100%" pt={1}>
                  <Text
                    fontSize="2xs"
                    color={colors.GRAY_DARK}
                    whiteSpace="nowrap"
                    textAlign={denso ? "right" : "center"}
                    transform={denso ? "rotate(-55deg)" : undefined}
                    transformOrigin={denso ? "top right" : undefined}
                    ml={denso ? "6px" : 0}
                  >
                    {moment(d.data).format("DD/MM")}
                  </Text>
                </Box>
              </Flex>
            </Tooltip>
          ))}
        </Flex>
      </Box>
    </Flex>
  );
}

function Indicador({
  rotulo,
  valor,
  detalhe,
}: {
  rotulo: string;
  valor: string;
  detalhe?: string;
}) {
  return (
    <Box>
      <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
        {rotulo}
      </Text>
      <Text fontSize="lg" fontWeight="bold" color="gray.700">
        {valor}
      </Text>
      {detalhe && (
        <Text fontSize="2xs" color="gray.500">
          {detalhe}
        </Text>
      )}
    </Box>
  );
}
